import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useEffect, useMemo, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader } from "@/components/ds";
import { ArrowDown, ArrowUp, Minus, Star } from "lucide-react";
import { apiGetJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { ServiceNicheMetrics, ServiceRevenueMetrics } from "@/lib/service-niche-metrics";
import type { ServiceReview } from "@/lib/db-service/service-reviews";
import { toast } from "sonner";

export const Route = createFileRoute("/app/metricas-servicios")({
  component: Page,
  head: () => ({ meta: [{ title: "Métricas de turnos" }] }),
});

const TONE: Record<ServiceNicheMetrics["noShowTone"], string> = {
  green: "border-emerald-400/50 bg-emerald-500/10 text-emerald-800",
  amber: "border-amber-400/50 bg-amber-500/10 text-amber-800",
  red: "border-red-400/50 bg-red-500/10 text-red-800",
  empty: "border-border/50 bg-card text-muted-foreground",
};

function formatMoney(amount: number) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(amount);
}

/** SCRUM-408: comparación vs. período anterior — signo importa según el KPI (noShow bajar es bueno). */
function DeltaBadge({ pct, invert = false }: { pct: number | null; invert?: boolean }) {
  if (pct == null) return <span className="text-[11px] text-muted-foreground">sin base de comparación</span>;
  const positive = invert ? pct < 0 : pct > 0;
  const neutral = pct === 0;
  const Icon = neutral ? Minus : pct > 0 ? ArrowUp : ArrowDown;
  const color = neutral ? "text-muted-foreground" : positive ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(pct)}% vs. período anterior
    </span>
  );
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<{
    metrics: ServiceNicheMetrics;
    revenue: ServiceRevenueMetrics;
    professionalNames: Record<string, string>;
    dateFrom: string;
    dateTo: string;
    comparison: { revenuePct: number | null; occupancyPct: number | null; chargedCountPct: number | null; noShowRatePct: number | null };
  } | null>(null);
  const [reviews, setReviews] = useState<ServiceReview[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    void apiGetJson<typeof data>(`/api/service-metrics?days=${days}`, tenantId)
      .then(setData)
      .catch((err) => toast.error(err instanceof Error ? err.message : "No se pudo cargar"));
    // SCRUM-408: reusa el mismo endpoint/data que ya expone /app/resenas, sin
    // recalcular el rating en el backend — filtramos por rango acá.
    void apiGetJson<{ items: ServiceReview[] }>("/api/service-admin?resource=reviews", tenantId)
      .then((r) => setReviews(r.items))
      .catch(() => setReviews([]));
  }, [tenantId, days]);

  const m = data?.metrics;
  const rev = data?.revenue;

  const reviewsInRange = useMemo(() => {
    if (!data) return [];
    return reviews.filter((r) => r.createdAt.slice(0, 10) >= data.dateFrom && r.createdAt.slice(0, 10) <= data.dateTo);
  }, [reviews, data]);
  const avgRating = reviewsInRange.length
    ? reviewsInRange.reduce((s, r) => s + r.rating, 0) / reviewsInRange.length
    : null;

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="metricas-servicios" />
      <CrudShellHeader
        title="Métricas de turnos"
        description="Ingresos, ocupación, no-show y satisfacción — sección propia, no el dashboard e-commerce."
      />
      <div className="flex gap-2 px-4 pb-4">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={`rounded-md border px-3 py-1 text-sm ${days === d ? "bg-primary text-primary-foreground" : ""}`}
            onClick={() => setDays(d)}
          >
            {d}d
          </button>
        ))}
      </div>
      {m && rev && data ? (
        <div className="grid gap-3 px-4 pb-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Ingresos</p>
            <p className="text-2xl font-semibold tabular-nums">{formatMoney(rev.totalRevenue)}</p>
            <p className="mt-1"><DeltaBadge pct={data.comparison.revenuePct} /></p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Ocupación</p>
            <p className="text-2xl font-semibold tabular-nums">{Math.round(m.occupancyOverallPct * 100)}%</p>
            <p className="mt-1"><DeltaBadge pct={data.comparison.occupancyPct} /></p>
          </div>
          <div className={`rounded-xl border p-4 ${TONE[m.noShowTone]}`}>
            <p className="text-xs uppercase tracking-wide">No-show</p>
            <p className="text-2xl font-semibold tabular-nums">
              {m.noShowRate == null ? "—" : `${Math.round(m.noShowRate * 100)}%`}
            </p>
            <p className="mt-1"><DeltaBadge pct={data.comparison.noShowRatePct} invert /></p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Satisfacción</p>
            <p className="flex items-center gap-1 text-2xl font-semibold tabular-nums">
              {avgRating == null ? "—" : avgRating.toFixed(1)}
              {avgRating != null ? <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> : null}
            </p>
            <p className="text-xs text-muted-foreground">{reviewsInRange.length} reseña{reviewsInRange.length === 1 ? "" : "s"} en el período</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Clientes nuevos</p>
            <p className="text-2xl font-semibold tabular-nums">{m.newCustomers}</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Recurrentes</p>
            <p className="text-2xl font-semibold tabular-nums">{m.returningCustomers}</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Turnos cobrados</p>
            <p className="text-2xl font-semibold tabular-nums">{rev.chargedCount}</p>
            <p className="mt-1"><DeltaBadge pct={data.comparison.chargedCountPct} /></p>
          </div>
        </div>
      ) : null}

      {rev?.byService.length ? (
        <div className="px-4 pb-8">
          <h3 className="mb-2 text-sm font-semibold">Servicios más rentables</h3>
          <ul className="divide-y rounded-xl border">
            {rev.byService.slice(0, 8).map((row) => (
              <li key={row.key} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>{row.label}</span>
                <span className="tabular-nums text-muted-foreground">
                  {formatMoney(row.revenue)} · {row.count} turno{row.count === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {m?.occupancy.length ? (
        <div className="px-4 pb-10">
          <h3 className="mb-2 text-sm font-semibold">Rendimiento por profesional</h3>
          <ul className="divide-y rounded-xl border">
            {m.occupancy.map((row) => {
              const revRow = rev?.byProfessional.find((r) => r.key === row.professionalId);
              return (
                <li key={row.professionalId} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{data?.professionalNames[row.professionalId] ?? row.professionalId}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {Math.round(row.occupancyPct * 100)}% ocupación
                    {revRow ? ` · ${formatMoney(revRow.revenue)}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </ClinicalPageShell>
  );
}
